import {
  getLenderProfileApprovalRetrieveQueryKey,
  useLenderProfileRetrieveSuspense,
  useLenderProfileUpdate,
} from "@boundless/bl-api-client/lender/services"
import { LenderProfileUpdateBody } from "@boundless/bl-api-client/lender/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { ButtonNext } from "@/components/buttons/ButtonNext"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormSection } from "@/components/form-elements/FormSection"
import { Form } from "@/components/ui/form"
import { Skeleton } from "@/components/ui/skeleton"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { z } from "zod"

type FormSchema = z.infer<typeof LenderProfileUpdateBody>

export function LenderProfileForm() {
  const queryClient = useQueryClient()
  const { data: lenderProfile, queryKey: profileQueryKey } =
    useLenderProfileRetrieveSuspense()

  const form = useForm<FormSchema>({
    resolver: zodResolver(LenderProfileUpdateBody),
    defaultValues: {
      name: lenderProfile.name,
      domain: lenderProfile.domain,
      highlight1: lenderProfile.highlight1,
      highlight2: lenderProfile.highlight2,
      highlight3: lenderProfile.highlight3,
      highlight4: lenderProfile.highlight4,
      highlight5: lenderProfile.highlight5,
    },
  })

  const { mutate: updateProfile } = useLenderProfileUpdate({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(profileQueryKey, data)

        const approvalQueryKey = getLenderProfileApprovalRetrieveQueryKey()
        queryClient.invalidateQueries({ queryKey: approvalQueryKey })

        toast.success("Changes submitted for approval.")
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
      },
    },
  })

  const submitForm = (details: FormSchema) => {
    updateProfile({ data: details })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitForm)}>
        <div className="@container/form flex flex-col gap-6">
          <FormSection>
            <FormInput name="name" placeholder="Legal Name" />
            <FormInput name="domain" placeholder="Website" />
          </FormSection>

          <FormInput name="highlight1" placeholder="Highlight 1" />
          <FormInput name="highlight2" placeholder="Highlight 2" />
          <FormInput name="highlight3" placeholder="Highlight 3" />
          <FormInput name="highlight4" placeholder="Highlight 4" />
          <FormInput name="highlight5" placeholder="Highlight 5" />
        </div>

        <div className="mt-8 flex flex-row justify-end">
          <ButtonNext>Submit</ButtonNext>
        </div>
      </form>
    </Form>
  )
}

export function FallbackLenderProfileForm() {
  return (
    <div className="flex flex-col gap-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <>
          <div key={i} className="grid grid-cols-2 gap-6">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
          <Skeleton className="h-12" />
        </>
      ))}
    </div>
  )
}
